import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown, Linkedin, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { PrimaryButton } from "./common";
import { NAV, SERVICE_GROUPS, BUSINESS } from "../data/site";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "./ui/accordion";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mega, setMega] = useState(false);
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobile(false); setMega(false); }, [loc.pathname]);

  const isHome = loc.pathname === "/";
  const solid = scrolled || !isHome;

  return (
    <>
      {/* Announcement bar */}
      <div className="bg-brand-evergreen text-white/90" data-testid="announcement-bar">
        <div className="container-page flex items-center justify-center gap-3 py-2.5 text-center text-[12.5px] sm:text-[13px]">
          <span className="hidden sm:inline">Supporting healthcare and professional documentation workflows from New Delhi to clients across India and international markets.</span>
          <span className="sm:hidden">New Delhi documentation & business support.</span>
          <Link to="/services" className="hidden shrink-0 items-center gap-1 font-600 text-brand-gold hover:underline md:inline-flex">
            Explore Our Services <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-colors duration-300 ${solid ? "border-b border-brand-border bg-white/95 shadow-[0_1px_20px_rgba(11,61,50,0.06)] backdrop-blur-md" : "bg-transparent"}`}
        data-testid="site-header"
      >
        <div className="container-page flex h-[68px] items-center justify-between gap-4">
          <Link to="/" aria-label="Primevanta Global home" data-testid="logo-link"><Logo /></Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
            {NAV.map((item) =>
              item.label === "Services" ? (
                <div key="services" className="relative" onMouseEnter={() => setMega(true)} onMouseLeave={() => setMega(false)}>
                  <button
                    className="flex items-center gap-1 rounded-full px-3.5 py-2 font-heading text-[14.5px] font-600 text-brand-ink transition-colors hover:text-brand-primary"
                    aria-expanded={mega} data-testid="nav-services"
                  >
                    Services <ChevronDown className={`h-4 w-4 transition-transform ${mega ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {mega && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-1/2 top-full z-[60] w-[720px] -translate-x-1/2 pt-3"
                        data-testid="mega-menu"
                      >
                        <div className="grid grid-cols-3 gap-x-6 gap-y-5 rounded-2xl border border-brand-border bg-white p-6 shadow-xl">
                          {SERVICE_GROUPS.map((g) => (
                            <div key={g.group}>
                              <p className="mb-2 font-heading text-[12px] font-700 uppercase tracking-wider text-brand-primary">{g.group}</p>
                              <ul className="space-y-1.5">
                                {g.items.slice(0, 5).map((it) => (
                                  <li key={it.title}>
                                    <Link to={`/services/${it.slug}`} className="text-[13.5px] text-brand-muted transition-colors hover:text-brand-ink">{it.title}</Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink
                  key={item.to} to={item.to}
                  className={({ isActive }) => `rounded-full px-3.5 py-2 font-heading text-[14.5px] font-600 transition-colors hover:text-brand-primary ${isActive ? "text-brand-primary" : "text-brand-ink"}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s|&/g, "")}`}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <a href={BUSINESS.linkedinProfile} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hidden h-10 w-10 items-center justify-center rounded-full border border-brand-border text-brand-muted transition-colors hover:border-brand-primary hover:text-brand-primary sm:flex" data-testid="linkedin-link">
              <Linkedin className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
            </a>
            <PrimaryButton to="/contact" className="hidden sm:inline-flex !py-3 !px-5" testid="cta-discuss-project">Discuss a Project</PrimaryButton>
            <button className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-border text-brand-ink" onClick={() => setMobile(true)} aria-label="Open menu" data-testid="mobile-menu-open">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobile && (
          <motion.div className="fixed inset-0 z-[60] lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-brand-evergreen/40 backdrop-blur-sm" onClick={() => setMobile(false)} />
            <motion.div
              className="absolute right-0 top-0 flex h-full w-[88%] max-w-sm flex-col bg-white shadow-2xl"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.3 }}
              data-testid="mobile-drawer"
            >
              <div className="flex items-center justify-between border-b border-brand-border px-5 py-4">
                <Logo />
                <button onClick={() => setMobile(false)} aria-label="Close menu" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-border" data-testid="mobile-menu-close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <Accordion type="single" collapsible className="w-full">
                  {NAV.map((item) =>
                    item.label === "Services" ? (
                      <AccordionItem value="services" key="services" className="border-brand-border">
                        <AccordionTrigger className="font-heading text-[16px] font-600 text-brand-ink hover:no-underline">Services</AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-4 pb-2">
                            {SERVICE_GROUPS.map((g) => (
                              <div key={g.group}>
                                <p className="mb-1.5 text-[11px] font-700 uppercase tracking-wider text-brand-primary">{g.group}</p>
                                <ul className="space-y-1">
                                  {g.items.slice(0, 4).map((it) => (
                                    <li key={it.title}><Link to={`/services/${it.slug}`} className="block py-1 text-[15px] text-brand-muted">{it.title}</Link></li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ) : (
                      <Link key={item.to} to={item.to} className="block border-b border-brand-border py-4 font-heading text-[16px] font-600 text-brand-ink">{item.label}</Link>
                    )
                  )}
                </Accordion>
              </div>
              <div className="border-t border-brand-border p-5">
                <PrimaryButton to="/contact" className="w-full" testid="mobile-cta-discuss">Discuss a Project</PrimaryButton>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
